

import { dutiesByRole } from "../../components/data/evaluationDuties";
import { ROLE_PAYLOAD_MAP } from "./payloadMappings";

export const ROLE_PT = "PT";

// Default values for the PT form data
// Default values for the PT form data (Base)
export const BASE_DEFAULT_VALUES = {
    // Header Info
    employeeId: "",
    employeeName: "",
    jobTitle: "",
    jobTitleFull: "",
    evaluationDate: "",
    reviewType: "Annual",
    evaluatorName: "",
    hireDate: "",

    // Rating Key
    selectedKey: "",

    // Comments
    dutiesCommentPage1: "",
    dutiesCommentPage2: "",
    dutiesCommentPage3: "",
    overallComments: "",
    targetedGoals: "",
    targetedGoalsPage1: "",
    targetedGoalsPage2: "",
    targetedGoalsPage3: "",

    // Signatures
    reviewerSignature: "",
    reviewerSignatureDate: "",
    reviewerSignatureImage: "",

    // Tardy/Absent Defaults
    daysTardy: "",
    daysAbsent: "",
    organizational2_daysTardy: "",
    organizational2_daysAbsent: "",
    organizational3_daysTardy: "",
    organizational3_daysAbsent: "",
};

export const PT_DEFAULT_VALUES = {
    ...BASE_DEFAULT_VALUES,
    jobTitle: "Physical Therapist",
    duty1: "", duty2: "", duty3: "", duty4: "", duty5: "",
    duty6: "", duty7: "", duty8: "", duty9: "", duty10: "",
    duty11: "", duty12: "", duty13: "", duty14: "", duty15: "",
    duty16: "", duty17: "", duty18: "", duty19: "", duty20: "",
    duty21: "", duty22: "", duty23: "", duty24: "", duty25: "",
};


export const getEvaluationDefaultValues = (role) => {
    // Base defaults
    const defaults = { ...BASE_DEFAULT_VALUES };

    // Set Job Title if known
    if (role && POSITION_NAMES[role.toLowerCase()]) {
        defaults.jobTitle = POSITION_NAMES[role.toLowerCase()];
    }

    // Role specific fields
    if (role === 'PT' || role === 'PTA' || !role) {
        return { ...PT_DEFAULT_VALUES };
    }

    const mapping = ROLE_PAYLOAD_MAP[role];
    if (mapping) {
        Object.keys(mapping).forEach(key => {
            defaults[key] = "";
        });
    }

    return defaults;
};


// Extract PT duties from the shared config specifically for easier iteration
export const PT_DUTIES_CONFIG = dutiesByRole[ROLE_PT].sections[0].items;
// The original file splits this into Page 1 (1-14) and Page 2 (15-25).
// We'll define the split index here.
// Role Display Names
export const POSITION_NAMES = {
    rn: "Registered Nurse (RN)",
    pt: "Physical Therapist (PT)",
    pta: "Physical Therapist Assistant (PTA)",
    ot: "Occupational Therapist (OT)",
    ota: "Occupational Therapist Assistant (OTA)",
    st: "Speech Language Pathologist (ST)",
    msw: "Medical Social Worker (MSW)",
    hha: "Certified Home Health Aide (HHA)",
    lvn: "Licensed Vocational Nurse (LVN)",
    "office-assistant": "Office Assistant (OA) ",
    oa: "Office Assistant (OA)",
    admin: "Administrative (ADMIN)",
};

// Map each role to its specific API endpoint and duties config
// Map each role to its specific API endpoint and duties config
export const ROLE_CONFIG = {
    PT: {
        apiEndpoint: "/evaluation-pt-pta",
        hasTargetedGoals: false,
        // Split PT duties into two sections for pagination consistency with other roles
        duties: [
            {
                title: "Essential Duties and Responsibilities (Part 1)",
                items: dutiesByRole.PT.sections[0].items.slice(0, 14)
            },
            {
                title: "Essential Duties and Responsibilities (Part 2)",
                items: dutiesByRole.PT.sections[0].items.slice(14)
            }
        ],
        pageBreakIndex: 1, // Split after 1st section
        schemaPrefix: "duty"
    },
    PTA: {
        apiEndpoint: "/evaluation-pt-pta",
        hasTargetedGoals: false,
        duties: [
            {
                title: "Essential Duties and Responsibilities (Part 1)",
                items: dutiesByRole.PT.sections[0].items.slice(0, 14)
            },
            {
                title: "Essential Duties and Responsibilities (Part 2)",
                items: dutiesByRole.PT.sections[0].items.slice(14)
            }
        ],
        pageBreakIndex: 1,
        schemaPrefix: "duty"
    },
    RN: {
        apiEndpoint: "/evaluation-rn",
        hasTargetedGoals: true,
        isThreePage: true,
        duties: null,
        pages: [
            [dutiesByRole.RN.sections[0], dutiesByRole.RN.sections[1], dutiesByRole.RN.sections[2]], // Page 1: Patient Care + Communication + Additional
            [dutiesByRole.RN.sections[3]], // Page 2: Organizational
            [dutiesByRole.RN.sections[4]]  // Page 3: Educational
        ],
        schemaPrefix: "duty"
    },
    HHA: {
        apiEndpoint: "/evaluation-hha",
        hasTargetedGoals: true,
        // HHA has 3 pages explicitly requested:
        // Page 1: A. Patient Care
        // Page 2: B. Organizational
        // Page 3: C. Education
        isThreePage: true, // Flag to trigger 3-page logic
        duties: null, // We'll use specific 'pages' config or sections index
        pages: [
            [dutiesByRole.HHA.sections[0]], // Page 1: Patient Care
            [dutiesByRole.HHA.sections[1]], // Page 2: Organizational
            [dutiesByRole.HHA.sections[2]]  // Page 3: Education
        ],
        schemaPrefix: "duty"
    },
    LVN: {
        apiEndpoint: "/evaluation-lvn",
        hasTargetedGoals: true,
        isThreePage: true,
        duties: null,
        pages: [
            [dutiesByRole.LVN.sections[0]], // Page 1: Patient Care
            [dutiesByRole.LVN.sections[1]], // Page 2: Organizational
            [dutiesByRole.LVN.sections[2]]  // Page 3: Education
        ],
        schemaPrefix: "duty"
    },
    MSW: {
        apiEndpoint: "/evaluation-msw",
        hasTargetedGoals: false,
        duties: [
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.MSW.sections[0].items.slice(0, 15)
            },
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.MSW.sections[0].items.slice(15)
            }
        ],
        pageBreakIndex: 1,
        schemaPrefix: "duty"
    },
    OT: {
        apiEndpoint: "/evaluation-ot-ota",
        hasTargetedGoals: false,
        duties: [
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.OT.sections[0].items.slice(0, 14)
            },
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.OT.sections[0].items.slice(14)
            }
        ],
        pageBreakIndex: 1, // Split after 1st section
        schemaPrefix: "duty"
    },
    OTA: {
        apiEndpoint: "/evaluation-ot-ota",
        hasTargetedGoals: false,
        duties: [
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.OT.sections[0].items.slice(0, 14)
            },
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.OT.sections[0].items.slice(14)
            }
        ],
        pageBreakIndex: 1,
        schemaPrefix: "duty"
    },
    ST: {
        apiEndpoint: "/evaluation-st",
        hasTargetedGoals: false,
        duties: [
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.ST.sections[0].items.slice(0, 15)
            },
            {
                title: "Essential Duties and Responsibilities ",
                items: dutiesByRole.ST.sections[0].items.slice(15)
            }
        ],
        pageBreakIndex: 1, // ST has 1 section.
        schemaPrefix: "duty"
    },
    OA: {
        apiEndpoint: "/evaluation-oa",
        hasTargetedGoals: true,
        isThreePage: true,
        duties: null,
        pages: [
            [dutiesByRole["Office Assistant"].sections[0]],
            [dutiesByRole["Office Assistant"].sections[1]],
            [dutiesByRole["Office Assistant"].sections[2]],
        ],
        schemaPrefix: "duty"
    }
};

// Helper to get config for current role
export const getRoleConfig = (role) => {
    if (!role) return ROLE_CONFIG.PT; // Default to PT
    const normalizedRole = String(role).trim().toUpperCase();
    const aliasMap = {
        "OFFICE-ASSISTANT": "OA",
        "OFFICE ASSISTANT": "OA",
    };
    const resolvedRole = aliasMap[normalizedRole] || normalizedRole;
    const key = Object.keys(ROLE_CONFIG).find(k => k.toLowerCase() === resolvedRole.toLowerCase());
    return ROLE_CONFIG[key] || ROLE_CONFIG.PT;
};

export const PT_PAGE_BREAK_INDEX = 14;

export const PERFORMANCE_COMMENTS = [
    { value: "Employee demonstrates excellent performance in all evaluated areas. Strong clinical skills and professional demeanor.", label: "Employee demonstrates excellent performance..." },
    { value: "Employee shows consistent performance with good understanding of job responsibilities. Meets expectations in all areas.", label: "Employee shows consistent performance..." },
    { value: "Employee performs well in most areas. Some areas need improvement and continued development.", label: "Employee performs well in most areas..." },
    { value: "Employee requires additional training and support in specific areas. Performance improvement plan recommended.", label: "Employee requires additional training..." },
    { value: "Employee demonstrates outstanding clinical skills and professionalism. Exceeds expectations consistently.", label: "Employee demonstrates outstanding clinical skills..." },
    { value: "Employee shows good progress and improvement. Continue to monitor and provide ongoing support.", label: "Employee shows good progress and improvement..." },
    { value: "Employee meets job requirements with satisfactory performance. No significant concerns noted.", label: "Employee meets job requirements..." },
    { value: "Employee displays strong communication skills and patient care. Well-regarded by patients and colleagues.", label: "Employee displays strong communication skills..." },
    { value: "Employee needs improvement in documentation and time management. Additional training recommended.", label: "Employee needs improvement in documentation..." },
    { value: "Employee demonstrates commitment to professional development and quality patient care.", label: "Employee demonstrates commitment to professional..." }
];

export const TARGETED_GOALS = [
    { value: "Continue to demonstrate excellence in patient care and maintain current level of performance.", label: "Continue to demonstrate excellence in patient care..." },
    { value: "Improve documentation accuracy and completeness. Focus on timely submission of clinical notes.", label: "Improve documentation accuracy and completeness..." },
    { value: "Enhance communication skills with interdisciplinary team members and improve care coordination.", label: "Enhance communication skills with interdisciplinary..." },
    { value: "Complete additional continuing education units in specialized areas of patient care.", label: "Complete additional continuing education units..." },
    { value: "Strengthen time management skills to improve efficiency in patient visits and documentation.", label: "Strengthen time management skills..." },
    { value: "Increase participation in agency in-service programs and professional development activities.", label: "Increase participation in agency in-service programs..." },
    { value: "Improve patient assessment skills and develop more comprehensive care plans.", label: "Improve patient assessment skills..." },
    { value: "Enhance knowledge of agency policies and procedures and ensure full compliance.", label: "Enhance knowledge of agency policies and procedures..." },
    { value: "Work on improving punctuality and attendance record.", label: "Work on improving punctuality and attendance record." },
    { value: "Develop leadership skills and take on additional responsibilities within the team.", label: "Develop leadership skills and take on additional..." },
    { value: "Focus on improving patient satisfaction scores through enhanced communication and care delivery.", label: "Focus on improving patient satisfaction scores..." },
    { value: "Complete certification or training in specialized areas relevant to home health care.", label: "Complete certification or training in specialized areas..." }
];

export const RATING_SET_NURSING = [
    { value: "4", label: "4 – Superior Performance" },
    { value: "3", label: "3 – Satisfactory Performance" },
    { value: "2", label: "2 – Inconsistent Performance" },
    { value: "1", label: "1 – Unacceptable Performance" },
];

export const RATING_SET_THERAPY = [
    { value: "1", label: "1 – Below Standard Level of Performance" },
    { value: "2", label: "2 – Meets Acceptable Level of Performance" },
    { value: "3", label: "3 – Exceeds Standard Level of Performance" },
    { value: "4", label: "4 – N/A for Evaluation Period" },
];

export const NURSING_ROLES = ["rn", "lvn", "hha", "oa", "office-assistant", "office assistant"];
export const getRatingSet = (role) => {
    if (!role) return RATING_SET_THERAPY;
    if (NURSING_ROLES.includes(role.toLowerCase())) {
        return RATING_SET_NURSING;
    }
    return RATING_SET_THERAPY;
};
